import { CRM_COMMUNICATION_TYPE_FILTER_VALUES } from './crmCommunicationTypeIcons';
import { uiT } from './uiTranslate';
import { mapCrmCommTicketsStatusToFilterStatus } from './crmCommunicationTicketFilters';

export const CRM_COMMUNICATION_PAGED_URL = '/api/crm_communication_tickets/paged';
export const SERVICE_TASK_SOURCE = 'service_task';
export const CRM_TICKET_SOURCE = 'crm_communication';
export const CRM_COMM_MASK_PORTAL_DETAIL = 'intr014';

const SERVICE_TASK_TYPE_VALUES = Object.freeze([
  'Notification', 'Info', 'Error', 'Task', 'TechChange', 'Permission', 'Need', 'Borrow', 'Emergency',
]);

export function isCrmTicketRow(rowData) {
  return rowData?.ticket_source === CRM_TICKET_SOURCE;
}

/** ID z Pusher/API payloadu service tasku – bez ticket_id (CRM). */
export function getServiceTaskIdFromEvent(data) {
  if (data == null) return null;
  const raw = data.id ?? data.task_id ?? data.task?.id ?? null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** ID z Pusher/API payloadu CRM tiketu (parita CrmCommunicationTicketsList). */
export function getCrmCommunicationTicketIdFromEvent(data) {
  if (data == null) return null;
  const raw = data.id ?? data.ticket_id ?? data.ticket?.id ?? null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Nájde CRM riadok v zmiešanom ServiceTask zozname (index list_row_id = crm:{id}). */
export function findCrmTicketRowInTable(table, ticketId) {
  if (!table?.getRow || ticketId == null) return null;
  const rowKey = getServiceTaskListRowKey(ticketId, CRM_TICKET_SOURCE);
  return table.getRow(rowKey)
    ?? table.getRows?.().find((r) => isCrmTicketRow(r.getData?.()) && Number(r.getData?.()?.id) === Number(ticketId))
    ?? null;
}

/** Aktualizuje stĺpec is_unread pre CRM tiket v ServiceTask liste. */
export async function updateCrmTicketRowIsUnread(table, ticketId, isUnread) {
  const row = findCrmTicketRowInTable(table, ticketId);
  if (!row) return false;
  const data = row.getData?.() || {};
  try {
    await row.update({ ...data, is_unread: !!isUnread });
  } catch (_) {
    return false;
  }
  return true;
}

export function getServiceTaskListRowKey(rowDataOrId, ticketSource) {
  if (rowDataOrId != null && typeof rowDataOrId === 'object') {
    if (rowDataOrId.list_row_id) return rowDataOrId.list_row_id;
    const src = rowDataOrId.ticket_source || SERVICE_TASK_SOURCE;
    return `${src === CRM_TICKET_SOURCE ? 'crm' : 'st'}:${rowDataOrId.id}`;
  }
  if (ticketSource === CRM_TICKET_SOURCE) return `crm:${rowDataOrId}`;
  if (ticketSource === SERVICE_TASK_SOURCE) return `st:${rowDataOrId}`;
  return `st:${rowDataOrId}`;
}

export function normalizeServiceTaskRow(row) {
  if (!row || isCrmTicketRow(row)) return row;
  return {
    ...row,
    ticket_source: SERVICE_TASK_SOURCE,
    list_row_id: `st:${row.id}`,
  };
}

function crmListUserShape(existingUser, displayName, username) {
  if (existingUser && typeof existingUser === 'object') return existingUser;
  const name = String(displayName ?? '').trim();
  const uname = String(username ?? '').trim();
  if (!name && !uname) return null;
  return {
    username: uname || name,
    employee: name ? { name } : undefined,
  };
}

export function normalizeCrmRowForServiceList(row) {
  if (!row) return row;
  const companyName = row.primary_company_name || '—';
  const createdName = row.created_by_name || row.created_by_username || '';
  const createdUsername = row.created_by_username || createdName;
  const touchedName = row.last_activity_user_name || row.last_activity_username || '';
  const touchedUsername = row.last_activity_username || row.created_by_username || touchedName;
  const createdByUser = crmListUserShape(row.created_by_user, createdName, createdUsername);
  const updatedByUser = crmListUserShape(
    row.updated_by_user,
    touchedName || createdName,
    touchedUsername || createdUsername,
  ) ?? createdByUser;
  return {
    ...row,
    ticket_source: CRM_TICKET_SOURCE,
    list_row_id: `crm:${row.id}`,
    created_by_user_id: row.created_by_user_id,
    defined_id: row.ticket_code ?? row.defined_id,
    theme: row.title ?? row.theme,
    machine: { name: companyName },
    'machine.name': companyName,
    waiting_for_action_deadline_at: row.waiting_for_action_deadline_at ?? row.deadline ?? null,
    deadline: row.deadline ?? row.waiting_for_action_deadline_at ?? null,
    updated_at: row.last_activity_at ?? row.updated_at ?? row.created_at,
    created_by_user: createdByUser,
    updated_by_user: updatedByUser,
  };
}

export function mapRowForStatusIndicator(rowData) {
  if (isCrmTicketRow(rowData)) {
    return {
      deadline: rowData?.deadline ?? rowData?.waiting_for_action_deadline_at ?? null,
      cancelled_at: rowData?.cancelled_at ?? null,
      waiting_for_action_deadline_at: rowData?.waiting_for_action_deadline_at ?? rowData?.deadline ?? null,
    };
  }
  return rowData;
}

export function statusIndicatorOptionsForRow(rowData) {
  if (isCrmTicketRow(rowData)) {
    return { settingsModule: 'crm', deadlineField: 'waiting_for_action_deadline_at' };
  }
  return { settingsModule: 'maintenance', deadlineField: 'deadline' };
}

function parseFilterTypeValues(filterType) {
  if (!filterType || filterType === 'All') return [];
  return String(filterType)
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

export function resolveTypeFilters(filterType) {
  const selected = parseFilterTypeValues(filterType);
  if (selected.length === 0) {
    return { fetchSt: true, fetchCrm: true, stTypeFilter: null, crmTypeFilter: null };
  }
  const stTypes = selected.filter((t) => SERVICE_TASK_TYPE_VALUES.includes(t));
  const crmTypes = selected.filter((t) => CRM_COMMUNICATION_TYPE_FILTER_VALUES.includes(t));
  return {
    fetchSt: stTypes.length > 0,
    fetchCrm: crmTypes.length > 0,
    stTypeFilter: stTypes.length ? stTypes.join(',') : null,
    crmTypeFilter: crmTypes.length ? crmTypes.join(',') : null,
  };
}

/** CRM API nepoužíva service_states filter_status_ids. */
export function shouldSkipCrmFetch(params) {
  if (!params) return true;
  const statusIds = String(params.filter_status_ids ?? '').trim();
  if (statusIds !== '') return true;
  return !resolveTypeFilters(params.filter_type).fetchCrm;
}

export function shouldSkipStFetch(params) {
  if (!params) return true;
  return !resolveTypeFilters(params.filter_type).fetchSt;
}

export function prepareServiceTaskFetchParams(params) {
  if (shouldSkipStFetch(params)) return null;
  const out = { ...params };
  const { stTypeFilter } = resolveTypeFilters(params.filter_type);
  if (stTypeFilter) out.filter_type = stTypeFilter;
  else delete out.filter_type;
  return out;
}

/**
 * Mapuje query parametre zo ServiceTask zoznamu na CRM paged API (portal_view).
 */
export function mapServiceTaskParamsToCrmParams(params) {
  const out = { ...params, portal_view: 1 };
  const fieldMap = {
    filter_defined_id: 'filter_ticket_code',
    filter_theme: 'filter_title',
    filter_machine: 'filter_primary_company_name',
    filter_updated_at: 'filter_last_activity_at',
  };
  Object.entries(fieldMap).forEach(([from, to]) => {
    if (out[from] != null && String(out[from]).trim() !== '') {
      out[to] = out[from];
    }
    delete out[from];
  });

  if (out.filter_status != null && String(out.filter_status).trim() !== '') {
    const mapped = mapCrmCommTicketsStatusToFilterStatus(out.filter_status);
    if (mapped) out.filter_status = mapped;
    else if (out.filter_status === 'Všetky') delete out.filter_status;
  }

  const { crmTypeFilter } = resolveTypeFilters(out.filter_type);
  if (crmTypeFilter) out.filter_type = crmTypeFilter;
  else delete out.filter_type;

  delete out.filter_status_ids;

  if (
    (out.filter_created_at && String(out.filter_created_at).trim())
    || (out.filter_last_activity_at && String(out.filter_last_activity_at).trim())
    || (out.filter_waiting_for_action_by && String(out.filter_waiting_for_action_by).trim())
  ) {
    if (!out.filter_date_format) {
      out.filter_date_format = out.filter_date_format || 'dd.MM.yyyy';
    }
  }

  return out;
}

export function dispatchServiceTaskListRowDetail(dispatch, rowData, t) {
  if (isCrmTicketRow(rowData)) {
    dispatch({
      type: 'create_new_tab',
      name: t('CrmCommunication.Tab.TicketDetail'),
      translationKey: 'CrmCommunication.Tab.TicketDetail',
      legacyLabel: t('CrmCommunication.Tab.TicketDetail'),
      page: 'CrmCommunicationTicketDetail',
      props: { ticketId: rowData.id, portalView: true },
      mask: CRM_COMM_MASK_PORTAL_DETAIL,
    });
    return;
  }
  dispatch({
    type: 'create_new_tab',
    name: uiT('Util.Tab.ServiceTaskDetail'),
    translationKey: 'Util.Tab.ServiceTaskDetail',
    legacyLabel: uiT('Util.Tab.ServiceTaskDetail'),
    page: 'ServiceTaskDetail',
    props: { task: rowData, taskId: rowData?.id },
    mask: 'intr004',
  });
}

export function crmRowPassesTopFilters(data, { contractorValue, typeValue, userId }) {
  if (typeValue && typeValue !== 'All' && String(data?.type ?? '') !== String(typeValue)) return false;
  if (contractorValue === 'Mnou vytvorené') {
    const createdBy = data?.created_by_user_id ?? data?.contractor_id;
    if (String(createdBy ?? '') !== String(userId ?? '')) return false;
  }
  return true;
}

export const CRM_TOP_TYPE_DROPDOWN_OPTIONS = [
  { value: 'announcement', labelKey: 'CrmCommunication.Type.announcement' },
  { value: 'acquisition', labelKey: 'CrmCommunication.Type.acquisition' },
  { value: 'quote_request', labelKey: 'CrmCommunication.Type.quote_request' },
];

export function createServiceTaskCrmMergedFetch(tabulatorGet) {
  return (stUrl, params) => tabulatorGet(stUrl, { ...params, include_crm: 1 });
}

export function getServiceTaskTypeFilterOptions(t) {
  const serviceTypes = [
    { label: t('ServiceTask.Type.Info'), value: 'Info' },
    { label: t('ServiceTask.Type.Error'), value: 'Error' },
    { label: t('ServiceTask.Type.Task'), value: 'Task' },
    { label: t('ServiceTask.Type.TechChange'), value: 'TechChange' },
    { label: t('ServiceTask.Type.Permission'), value: 'Permission' },
    { label: t('ServiceTask.Type.Need'), value: 'Need' },
    { label: t('ServiceTask.Type.Borrow'), value: 'Borrow' },
    { label: t('ServiceTask.Type.Emergency'), value: 'Emergency' },
  ];
  const crmTypes = [
    { label: t('CrmCommunication.Type.announcement'), value: 'announcement' },
    { label: t('CrmCommunication.Type.acquisition'), value: 'acquisition' },
    { label: t('CrmCommunication.Type.quote_request'), value: 'quote_request' },
  ];
  return [
    { label: t('ServiceTask.Type.Notification'), value: 'Notification' },
    ...serviceTypes,
    ...crmTypes,
  ].sort((a, b) => a.label.localeCompare(b.label, 'sk'));
}
